import React, { useState, useEffect } from "react";
import { submitUserForm } from "../api/usersApi";
import { sendSupportNotification } from "../services/sendNotification";
import { toast } from "react-toastify";
import { countries } from "country-data-list";

// Custom CSS for animations
const animationCSS = `
  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    10%, 30%, 50%, 70%, 90% { transform: translateX(-8px); }
    20%, 40%, 60%, 80% { transform: translateX(8px); }
  }
  
  @keyframes pulse-error {
    0%, 100% { background-color: rgba(248, 113, 113, 0.1); border-color: rgb(248, 113, 113); }
    50% { background-color: rgba(248, 113, 113, 0.2); border-color: rgb(239, 68, 68); }
  }
  
  @keyframes glow-error {
    0%, 100% { box-shadow: 0 0 5px 2px rgba(239, 68, 68, 0.3); }
    50% { box-shadow: 0 0 8px 3px rgba(239, 68, 68, 0.5); }
  }
  
  .animate-shake {
    animation: shake 0.6s cubic-bezier(.36,.07,.19,.97) both;
  }
  
  .animate-pulse-error {
    animation: pulse-error 1.5s ease-in-out infinite;
  }
  
  .animate-glow-error {
    animation: glow-error 1.5s ease-in-out infinite;
  }
  
  .field-required-error {
    transition: all 0.3s;
    border-color: rgb(248, 113, 113) !important;
    background-color: rgba(254, 242, 242, 0.8) !important;
    box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.4) !important;
  }

  .dark .field-required-error {
    border-color: rgb(248, 113, 113) !important;
    background-color: rgba(127, 29, 29, 0.4) !important;
    box-shadow: 0 0 0 2px rgba(248, 113, 113, 0.4) !important;
  }
`;

const AlumniJoinForm = ({ setShowJoinForm }) => {
  const [formData, setFormData] = useState({
    name: "",
    designation: "",
    companyOrInstitute: "",
    permanentResidency: "",
    fieldsOfExperience: "",
    rolesOfExperience: "",
    category: "",
    phoneNumber: "",
    linkedinUrl: "",
    websiteUrl: "",
    email: "",
    profileImage: null,
  });

  const [errors, setErrors] = useState({});
  const [code, setCode] = useState("");
  const [number, setNumber] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // Add the custom animation styles when component mounts
  useEffect(() => {
    // Create style element
    const styleEl = document.createElement("style");
    styleEl.id = "form-animations";
    styleEl.textContent = animationCSS;
    document.head.appendChild(styleEl);

    // Cleanup on component unmount
    return () => {
      const styleToRemove = document.getElementById("form-animations");
      if (styleToRemove) {
        document.head.removeChild(styleToRemove);
      }
    };
  }, []); // Predefined options for dropdowns
  const categoryOptions = [
    "Recent Graduate (0-2 years)",
    "Mid-Career Professional (3-10 years)",
    "Senior Professional (10+ years)",
    "Entrepreneur/Founder",
    "Academic/Researcher",
    "Government/Public Sector",
    "Retired",
  ];

  const countryList = countries.all.sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing and remove error styling
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));

      // Remove all error styling if present
      const inputElement = document.getElementById(name);
      if (inputElement) {
        // Remove all new error classes
        inputElement.classList.remove("field-required-error");
        inputElement.classList.remove("animate-glow-error");
        inputElement.classList.remove("animate-pulse-error");

        // Remove old error classes too
        inputElement.classList.remove("border-red-400", "dark:border-red-500");
        inputElement.classList.remove("text-red-600", "dark:text-red-400");
        inputElement.classList.remove(
          "placeholder-red-400",
          "dark:placeholder-red-500"
        );
        inputElement.classList.remove(
          "focus:ring-red-200",
          "dark:focus:ring-red-900/30"
        );

        // Reset placeholder
        if (name === "name") {
          inputElement.placeholder = "Enter your full name";
        } else if (name === "email") {
          inputElement.placeholder = "Enter your email address";
        } else if (name === "designation") {
          inputElement.placeholder = "Enter your current job title";
        } else if (name === "companyOrInstitute") {
          inputElement.placeholder = "Enter your company or institute name";
        } else if (name === "category") {
          inputElement.placeholder = "Select your professional category";
        } else {
          inputElement.placeholder = ""; // Default placeholder
        }

        // Add back the appropriate normal styling based on the section
        const parentSection = inputElement.closest("div.p-8, div.p-6");
        if (parentSection) {
          if (
            parentSection.classList.contains("bg-white") ||
            parentSection.classList.contains("from-blue-50")
          ) {
            inputElement.classList.add(
              "border-blue-200",
              "dark:border-blue-800/40"
            );
          } else if (parentSection.classList.contains("from-indigo-50")) {
            inputElement.classList.add(
              "border-indigo-200",
              "dark:border-indigo-800/40"
            );
          } else if (parentSection.classList.contains("from-purple-50")) {
            inputElement.classList.add(
              "border-purple-200",
              "dark:border-purple-800/40"
            );
          } else {
            // Default styling
            inputElement.classList.add(
              "border-blue-200",
              "dark:border-blue-800/40"
            );
          }
        }

        // Remove error icon from label if it exists
        const parentGroup = inputElement.closest(".relative.group");
        if (parentGroup) {
          const errorIcon = parentGroup.querySelector(".error-icon");
          if (errorIcon) {
            errorIcon.remove();
          }

          // Remove error message banner if it exists
          const errorMsg = parentGroup.querySelector(
            ".bg-red-100, .dark\\:bg-red-900\\/30"
          );
          if (errorMsg) {
            errorMsg.remove();
          }
        }
      }
    }
  };

  const handleArrayInputChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];

    // Clear any previous errors first
    setErrors((prev) => ({
      ...prev,
      profileImage: "",
    }));

    if (file) {
      // Validate file type
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
      ];
      if (!allowedTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          profileImage:
            "Please select a valid image file (JPEG, PNG, GIF, or WebP)",
        }));
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({
          ...prev,
          profileImage: "File size must be less than 5MB",
        }));
        return;
      }

      // Successfully set the file
      setFormData((prev) => ({
        ...prev,
        profileImage: file,
      }));
    } else {
      // No file selected, clear the profile image
      setFormData((prev) => ({
        ...prev,
        profileImage: null,
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    let hasRequiredFieldErrors = false;

    // Personal Information - Required Fields
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
      hasRequiredFieldErrors = true;
      // Focus on the first required field with error
      if (!hasRequiredFieldErrors) {
        setTimeout(() => {
          document.getElementById("name").focus();
        }, 100);
      }
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
      hasRequiredFieldErrors = true;
      if (!hasRequiredFieldErrors) {
        setTimeout(() => {
          document.getElementById("email").focus();
        }, 100);
      }
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
      if (!hasRequiredFieldErrors) {
        setTimeout(() => {
          document.getElementById("email").focus();
        }, 100);
      }
    }

    // Professional Information - Required Fields
    if (!formData.designation.trim()) {
      newErrors.designation = "Designation is required";
      hasRequiredFieldErrors = true;
      if (!hasRequiredFieldErrors) {
        setTimeout(() => {
          document.getElementById("designation").focus();
        }, 100);
      }
    }

    if (!formData.companyOrInstitute.trim()) {
      newErrors.companyOrInstitute = "Company or Institute is required";
      hasRequiredFieldErrors = true;
      if (!hasRequiredFieldErrors) {
        setTimeout(() => {
          document.getElementById("companyOrInstitute").focus();
        }, 100);
      }
    }

    if (!formData.category) {
      newErrors.category = "Category is required";
      hasRequiredFieldErrors = true;
      if (!hasRequiredFieldErrors) {
        setTimeout(() => {
          document.getElementById("category").focus();
        }, 100);
      }
    }

    // Academic Information - Required Fields
    if (!formData.batch) {
      newErrors.batch = "Batch is required";
      hasRequiredFieldErrors = true;
      if (!hasRequiredFieldErrors) {
        setTimeout(() => {
          document.getElementById("batch").focus();
        }, 100);
      }
    }

    if (!formData.academicCategory) {
      newErrors.academicCategory = "Academic Category is required";
      hasRequiredFieldErrors = true;
      if (!hasRequiredFieldErrors) {
        setTimeout(() => {
          document.getElementById("academicCategory").focus();
        }, 100);
      }
    }

    if (!formData.academicQualifications) {
      newErrors.academicQualifications = "Academic Qualifications is required";
      hasRequiredFieldErrors = true;
      if (!hasRequiredFieldErrors) {
        setTimeout(() => {
          document.getElementById("academicQualifications").focus();
        }, 100);
      }
    }

    // Check if "Other" is selected and requires additional information
    if (
      formData.academicCategory === "Other" &&
      !formData.academicCategoryOther
    ) {
      newErrors.academicCategoryOther = "Please specify your academic category";
      if (!hasRequiredFieldErrors) {
        setTimeout(() => {
          document.getElementById("academicCategoryOther").focus();
        }, 100);
      }
    }

    // Optional Fields with Format Validation
    if (
      // formData.phoneNumber &&
      // !/^[+]?[1-9][\d]{0,15}$/.test(
      //   formData.phoneNumber.replace(/[\s\-()]/g, "")
      // )
      formData.phoneNumber &&
      !/^\+?[1-9]\d{1,14}$/.test(formData.phoneNumber.replace(/[\s\-()]/g, ""))
    ) {
      newErrors.phoneNumber = "Phone number is invalid";
    }

    if (
      formData.linkedinUrl &&
      !formData.linkedinUrl.includes("linkedin.com")
    ) {
      newErrors.linkedinUrl = "Please enter a valid LinkedIn URL";
    }

    // if (formData.websiteUrl && !formData.websiteUrl.match(/^https?:\/\/.+/)) {
    //   newErrors.websiteUrl =
    //     "Please enter a valid website URL (include http:// or https://)";
    // }

    if (
      formData.websiteUrl &&
      !/^((https?:\/\/)?[^\s/$.?#]+\.[^\s]+)$/i.test(formData.websiteUrl)
    ) {
      newErrors.websiteUrl = "Please enter a valid website URL";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate the form
    const isValid = validateForm();

    if (!isValid) {
      // Highlight all fields with errors
      const errorFields = Object.keys(errors);

      // Apply much more visible highlighting to all error fields
      errorFields.forEach((fieldName) => {
        const errorElement = document.getElementById(fieldName);
        if (errorElement) {
          // First, remove any existing classes
          errorElement.classList.remove(
            "border-blue-200",
            "dark:border-blue-800/40"
          );
          errorElement.classList.remove(
            "border-indigo-200",
            "dark:border-indigo-800/40"
          );
          errorElement.classList.remove(
            "border-purple-200",
            "dark:border-purple-800/40"
          );

          // Add strong error styling
          errorElement.classList.add("field-required-error");
          errorElement.classList.add("animate-glow-error");

          // Add a prominent error icon next to the field label if not already present
          const parentGroup = errorElement.closest(".relative.group");
          if (parentGroup) {
            // Find the label for this field
            const label = parentGroup.querySelector("label");
            if (label && !label.querySelector(".error-icon")) {
              const errorIcon = document.createElement("span");
              errorIcon.className =
                "error-icon inline-flex items-center justify-center ml-2 text-red-500 dark:text-red-400";
              errorIcon.innerHTML = `
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                </svg>
              `;
              label.appendChild(errorIcon);
            }
          }

          // Add pulse animation to the placeholder text
          if (
            errorElement.tagName === "INPUT" ||
            errorElement.tagName === "SELECT" ||
            errorElement.tagName === "TEXTAREA"
          ) {
            errorElement.placeholder = "This field is required";
            errorElement.setAttribute("aria-invalid", "true");
          }
        }
      });

      // Find the first error element and scroll to it
      const firstErrorField = errorFields[0];
      if (firstErrorField) {
        const errorElement = document.getElementById(firstErrorField);
        if (errorElement) {
          // Scroll to the element with a slight delay to ensure DOM updates
          setTimeout(() => {
            errorElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
            errorElement.focus();
          }, 100);

          // Add a shake animation to the form section containing the error
          const parentSection = errorElement.closest(".relative.group");
          if (parentSection) {
            parentSection.classList.add("animate-shake");
            setTimeout(() => {
              parentSection.classList.remove("animate-shake");
            }, 1000);

            // Add a more visible error message at the top of the parent section
            const errorMsg = document.createElement("div");
            errorMsg.className =
              "bg-red-100 dark:bg-red-900/30 border-l-4 border-red-500 text-red-700 dark:text-red-300 p-3 mb-4 rounded animate-pulse-error";
            errorMsg.innerHTML = `
              <div class="flex items-center">
                <svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                </svg>
                <p><strong>Required field:</strong> ${errors[firstErrorField]}</p>
              </div>
            `;

            // Only add if not already present
            const existingError = parentSection.querySelector(
              ".bg-red-100, .dark\\:bg-red-900\\/30"
            );
            if (!existingError) {
              parentSection.prepend(errorMsg);

              // Remove after some time
              setTimeout(() => {
                if (parentSection.contains(errorMsg)) {
                  parentSection.removeChild(errorMsg);
                }
              }, 5000);
            }
          }
        }
      }

      // Show the validation error summary at the top of the form
      const validationSummary = document.getElementById(
        "form-validation-summary"
      );
      if (validationSummary) {
        validationSummary.classList.remove("hidden");
        validationSummary.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });

        // Add list of specific errors to the summary
        const errorList = document.createElement("ul");
        errorList.className = "list-disc pl-5 mt-2 space-y-1";

        // Limit to first 3 errors to avoid overwhelming the user
        errorFields.slice(0, 3).forEach((field) => {
          const li = document.createElement("li");
          li.textContent = errors[field];
          errorList.appendChild(li);
        });

        // Add the error list if not already present
        const errorContainer = validationSummary.querySelector(
          ".text-red-700, .dark\\:text-red-300"
        );
        if (errorContainer && !errorContainer.querySelector("ul")) {
          errorContainer.appendChild(errorList);
        }

        // Hide after 8 seconds
        setTimeout(() => {
          validationSummary.classList.add("hidden");
        }, 8000);
      }

      // Show toast notification for validation errors with more detailed message
      toast.error("Please fill in all required fields marked in red", {
        position: "top-center",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
          backgroundColor: "#FEE2E2",
          color: "#B91C1C",
          fontWeight: "bold",
        },
        progressStyle: {
          backgroundColor: "#EF4444",
        },
      });

      return;
    }

    setIsSubmitting(true);

    try {
      // Convert comma-separated strings to arrays for backend
      const dataToSubmit = {
        ...formData,
        fieldsOfExperience: formData.fieldsOfExperience
          ? formData.fieldsOfExperience
              .split(",")
              .map((item) => item.trim())
              .filter(Boolean)
          : [],
        rolesOfExperience: formData.rolesOfExperience
          ? formData.rolesOfExperience
              .split(",")
              .map((item) => item.trim())
              .filter(Boolean)
          : [],
        academicCategory:
          formData.academicCategory === "Other"
            ? formData.academicCategoryOther
            : formData.academicCategory,
        academicQualifications: formData.academicQualifications
          ? formData.academicQualifications
              .split(",")
              .map((item) => item.trim())
              .filter(Boolean)
          : [],
      };

      const formDataToSend = new FormData();
      Object.keys(dataToSubmit).forEach((key) => {
        if (key === "profileImage" && dataToSubmit[key]) {
          formDataToSend.append("profileImage", dataToSubmit[key]);
        } else {
          formDataToSend.append(key, dataToSubmit[key]);
        }
      });

      // ✅ Call backend API
      const result = await submitUserForm(formDataToSend);

      // Notify the admin/support team about new alumni join request
      const res = await sendSupportNotification({ name: formDataToSend.name });
      if (res.status === 200) {
        toast.success(
          "🎉 Thank you for joining our alumni network! We will review your application soon."
        );
      }

      setSuccessMessage(
        result.message ||
          "🎉 Thank you for joining our alumni network! We will review your application soon."
      );
      // Reset form
      setFormData({
        name: "",
        designation: "",
        companyOrInstitute: "",
        permanentResidency: "",
        fieldsOfExperience: "",
        rolesOfExperience: "",
        category: "",
        phoneNumber: "",
        linkedinUrl: "",
        websiteUrl: "",
        email: "",
        profileImage: null,
      });
      // Reset code and number states
      setCode("");
      setNumber("");
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(
        error?.message ||
          "❌ There was an error submitting your application. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      {/* Page Header */}
      <div className="text-center mb-8 sm:mb-12 relative pt-4">
        {/* <div className="absolute top-0 left-1/2 -translate-x-1/2 transform -translate-y-1/2 w-16 sm:w-20 h-16 sm:h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-xl border-4 border-white dark:border-blue-950">
          <svg
            className="w-8 h-8 sm:w-10 sm:h-10 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
            />
          </svg>
        </div> */}

        <div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-black mb-4">
            Join Our Alumni Network
          </h1>
          <div className="w-24 sm:w-32 h-1 bg-gradient-to-r from-blue-500  mx-auto mb-4 sm:mb-6 rounded-full"></div>
        </div>
      </div>

      <div>
        {successMessage && (
          <div className="relative mb-10 flex flex-col items-center">
            {/* Success Card */}
            <div className="relative z-20 mt-20 bg-gradient-to-br from-blue-50 via-white to-blue-100 dark:from-blue-900/40 dark:via-blue-950 dark:to-blue-900/20 border-2 border-blue-400/50 dark:border-blue-700/50 rounded-[2rem] px-6 sm:px-10 py-14 max-w-2xl w-full text-center shadow-xl">
              {/* Success Emblem */}
              <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-24 h-24 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 p-1 shadow-lg">
                <div className="w-full h-full rounded-full bg-white dark:bg-blue-950 flex items-center justify-center">
                  <svg
                    className="w-12 h-12 text-blue-600 dark:text-blue-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      strokeWidth="2"
                      stroke="currentColor"
                      fill="none"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="3"
                      d="M9 12l2 2 4-4"
                      stroke="#22c55e"
                    />
                  </svg>
                </div>
              </div>

              {/* Content */}
              <div className="flex flex-col items-center gap-5 pt-5">
                <div className="space-y-4">
                  <h3 className="text-3xl sm:text-4xl font-extrabold text-black tracking-tight">
                    Application Submitted!
                  </h3>
                  <div className="w-24 h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 mx-auto rounded-full"></div>
                  <p className="text-lg text-blue-800 dark:text-blue-200 font-medium leading-relaxed max-w-xl mx-auto">
                    {successMessage}
                  </p>
                </div>

                {/* Animated Badge */}
                <div className="mt-6 relative">
                  <span className="inline-flex items-center px-5 py-2 rounded-full bg-gradient-to-r from-blue-100 to-indigo-100 dark:from-blue-900/40 dark:to-indigo-900/40 text-blue-800 dark:text-blue-200 text-sm sm:text-base font-semibold tracking-wide shadow-sm relative z-10">
                    <svg
                      className="w-5 h-5 mr-1.5 text-blue-600 dark:text-blue-400"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Thank you for joining our alumni network!
                  </span>
                </div>

                {/* Call to Action */}
                <div className="mt-8 relative group inline-block z-10">
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl blur-sm opacity-70 group-hover:opacity-100 transition duration-200"></div>
                  <button
                    onClick={() => {
                      setShowJoinForm(false);
                      // Small delay to ensure modal is closed before scrolling
                      setTimeout(() => {
                        const profileSection =
                          document.getElementById("profile");
                        if (profileSection) {
                          profileSection.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                          });
                        }
                      }, 300);
                    }}
                    className="relative px-8 py-3.5 bg-white dark:bg-blue-900 rounded-xl text-blue-700 dark:text-blue-300 text-lg font-bold flex items-center gap-2 shadow-lg hover:shadow-xl transition-shadow duration-200"
                  >
                    View Alumni Network
                    <svg
                      className="w-5 h-5 ml-1 group-hover:translate-x-1 transition-transform duration-200"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M13 7l5 5m0 0l-5 5m5-5H6"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {!successMessage && (
        <form onSubmit={handleSubmit} className="space-y-10">
          {/* Form validation error summary - only shows when there are errors */}
          <div id="form-validation-summary" className="hidden">
            <div className="bg-red-50 dark:bg-red-900/30 border-l-4 border-red-500 p-4 rounded-lg shadow-lg animate-pulse-error mb-6">
              <div className="flex items-start">
                <div className="flex-shrink-0 pt-0.5">
                  <svg
                    className="h-6 w-6 text-red-500"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-red-800 dark:text-red-200">
                    Please complete all required fields
                  </h3>
                  <div className="mt-2 text-red-700 dark:text-red-300">
                    <p>
                      Some required information is missing or incorrect. Please
                      check the fields marked in red and try again.
                    </p>
                    <ul className="mt-3 list-disc list-inside space-y-1">
                      {Object.entries(errors).map(([field, message]) => (
                        <li key={field} className="text-sm">
                          <span className="font-semibold">
                            {field.charAt(0).toUpperCase() + field.slice(1)}:
                          </span>{" "}
                          {message}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Profile Image Section */}
          <div className="p-6 sm:p-8 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl border border-blue-100 dark:border-blue-800/30 shadow-lg mb-10">
            <div className="flex flex-col items-center justify-center space-y-6 mt-6 sm:mt-8">
              <div className="relative group mx-auto">
                <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-full border-4 border-white dark:border-blue-800 overflow-hidden bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/40 dark:to-indigo-800/40 shadow-[0_8px_20px_rgba(59,130,246,0.3)] group-hover:shadow-[0_10px_25px_rgba(59,130,246,0.4)] transition-all duration-300">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-indigo-600/20 dark:from-blue-400/20 dark:to-indigo-500/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                  {formData.profileImage ? (
                    <img
                      src={URL.createObjectURL(formData.profileImage)}
                      alt="Profile preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full">
                      <svg
                        className="w-16 h-16 text-blue-400 dark:text-blue-300"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <p className="text-sm font-medium text-blue-500 dark:text-blue-300 mt-2">
                        Profile Image
                      </p>
                    </div>
                  )}
                </div>
                {formData.profileImage && (
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center border-2 border-white dark:border-blue-900 shadow-md">
                    <svg
                      className="w-5 h-5 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                )}
              </div>

              <div className="flex flex-col items-center justify-center w-full space-y-4 mt-4">
                <div className="flex justify-center w-full">
                  <label
                    htmlFor="profileImage"
                    className="cursor-pointer text-black"
                  >
                    <button
                      type="button"
                      className={`px-8 py-3 rounded-xl font-medium text-white shadow-lg transition-all duration-300 flex items-center justify-center ${
                        formData.profileImage
                          ? "bg-gradient-to-r from-indigo-600 to-blue-500 hover:from-indigo-700 hover:to-blue-600 shadow-blue-500/30"
                          : "bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 shadow-indigo-500/30"
                      }`}
                      onClick={() =>
                        document.getElementById("profileImage").click()
                      }
                    >
                      <svg
                        className="w-5 h-5 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d={
                            formData.profileImage
                              ? "M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                              : "M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                          }
                        />
                      </svg>
                      {formData.profileImage ? "Change Photo" : "Upload Photo"}
                    </button>
                  </label>
                  <input
                    type="file"
                    id="profileImage"
                    name="profileImage"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                    formNoValidate
                  />
                </div>

                <div className="text-center">
                  <p className="text-sm text-blue-600 dark:text-blue-300 bg-blue-50/50 dark:bg-blue-900/20 px-4 py-2 rounded-lg inline-block">
                    <span className="font-medium">Accepted formats:</span> JPEG,
                    PNG, GIF, WebP (Max 5MB)
                  </p>
                </div>

                {formData.profileImage && (
                  <div className="flex items-center justify-center gap-2 text-sm text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-4 py-2 rounded-lg w-full max-w-xs mx-auto">
                    <svg
                      className="w-5 h-5 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="font-medium mr-1">Image uploaded:</span>
                    <span className="truncate max-w-[150px]">
                      {formData.profileImage.name}
                    </span>
                  </div>
                )}

                {errors.profileImage && (
                  <div className="flex items-center justify-center gap-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-4 py-2 rounded-lg w-full max-w-xs mx-auto">
                    <svg
                      className="w-5 h-5 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {errors.profileImage}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Personal Information Section */}
          <div className="p-6 sm:p-8 bg-white dark:bg-blue-950/60 rounded-2xl border border-blue-100 dark:border-blue-800/30 shadow-xl mb-8 sm:mb-10">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 mb-5 sm:mb-6 pb-4 border-b border-blue-100 dark:border-blue-800/30">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/20 mx-auto sm:mx-0">
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </div>
              <div className="text-center sm:text-left">
                <h3 className="text-xl sm:text-2xl font-bold text-black">
                  Personal Information
                </h3>
                <p className="text-gray-600 dark:text-gray-300 text-xs sm:text-sm">
                  Tell us about yourself so we can connect you with the
                  community
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
              {/* Name */}
              <div className="relative group">
                <label
                  htmlFor="name"
                  className="flex items-center gap-1 text-xs sm:text-sm font-medium mb-2"
                >
                  <svg
                    className="w-3 h-3 sm:w-4 sm:h-4 text-blue-500 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="truncate text-gray-900 dark:text-gray-100">
                    Full Name
                  </span>
                  <span className="text-red-500 dark:text-red-400 font-bold text-lg">
                    *
                  </span>
                  <span className="ml-1 text-xs font-semibold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 px-2 py-0.5 rounded-full">
                    Required
                  </span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <svg
                      className={`w-4 h-4 sm:w-5 sm:h-5 ${
                        errors.name
                          ? "text-red-400 dark:text-red-500"
                          : "text-gray-400 dark:text-gray-500"
                      }`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9 6a3 3 0 100-6 3 3 0 000 6zM17 6a3 3 0 100-6 3 3 0 000 6zM12.93 8C12.98 8.33 13 8.66 13 9H19V7c0-2-4-3-6-3C12.12 4 11.37 4.2 10.74 4.5 11.55 5.05 12 5.95 12 7v1c0 .34-.03.67-.07 1h1zM0 9v2c0 2 4 3 6 3s6-1 6-3V9c0-2-4-3-6-3S0 7 0 9zm10 2c0 .83-.67 1.5-1.5 1.5S7 11.83 7 11s.67-1.5 1.5-1.5S10 10.17 10 11zm-3.38.03A5.37 5.37 0 016 11c-1.03 0-3.55.29-4 1 .14.28 .38.5 .64.71.84-.22 2.06-.48 3.36-.48s2.52.26 3.36.48c.26-.21.5-.43.64-.71-.45-.71-2.97-1-4-1a5.4 5.4 0 01-.38.03z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={`w-full pl-9 sm:pl-10 px-3 sm:px-4 py-2 sm:py-3 text-sm border-2 rounded-lg sm:rounded-xl transition-all duration-300 bg-gray-50 dark:bg-gray-800/40 focus:bg-white dark:focus:bg-gray-800 focus:outline-none ${
                      errors.name
                        ? "border-red-400 dark:border-red-500 text-red-600 dark:text-red-400 placeholder-red-400 dark:placeholder-red-500 focus:ring-2 focus:ring-red-200 dark:focus:ring-red-900/30 animate-pulse-once"
                        : "border-blue-200 dark:border-blue-800/40 text-gray-900 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-4 focus:ring-blue-100 dark:focus:ring-blue-900/30 focus:border-blue-500 dark:focus:border-blue-500"
                    }`}
                    placeholder={
                      errors.name ? "Required field" : "Enter your full name"
                    }
                    aria-required="true"
                    aria-invalid={errors.name ? "true" : "false"}
                  />
                  {errors.name && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <svg
                        className="w-5 h-5 text-red-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  )}
                </div>
                {errors.name && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400 flex items-center gap-1 font-medium animate-fade-in">
                    <svg
                      className="w-4 h-4 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {errors.name}
                  </p>
                )}
              </div>

              {/* Email */}
              <div className="relative group">
                <label
                  htmlFor="email"
                  className="flex items-center gap-1 text-xs sm:text-sm font-medium mb-2 text-black"
                >
                  <svg
                    className="w-3 h-3 sm:w-4 sm:h-4 text-blue-500 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                  <span className="truncate text-gray-900 dark:text-gray-100">
                    Email Address
                  </span>
                  <span className="text-red-500 dark:text-red-400 font-bold text-lg">
                    *
                  </span>
                  <span className="ml-1 text-xs font-semibold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 px-2 py-0.5 rounded-full">
                    Required
                  </span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <svg
                      className={`w-4 h-4 sm:w-5 sm:h-5 ${
                        errors.email
                          ? "text-red-400 dark:text-red-500"
                          : "text-gray-400 dark:text-gray-500"
                      }`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                      <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                    </svg>
                  </div>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full pl-9 sm:pl-10 px-3 sm:px-4 py-2 sm:py-3 text-sm border-2 rounded-lg sm:rounded-xl transition-all duration-300 bg-gray-50 dark:bg-gray-800/40 focus:bg-white dark:focus:bg-gray-800 focus:outline-none ${
                      errors.email
                        ? "border-red-400 dark:border-red-500 text-red-600 dark:text-red-400 placeholder-red-400 dark:placeholder-red-500 focus:ring-2 focus:ring-red-200 dark:focus:ring-red-900/30 animate-pulse-once"
                        : "border-blue-200 dark:border-blue-800/40 text-gray-900 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-4 focus:ring-blue-100 dark:focus:ring-blue-900/30 focus:border-blue-500 dark:focus:border-blue-500"
                    }`}
                    placeholder={
                      errors.email
                        ? "Required field"
                        : "Enter your email address"
                    }
                    aria-required="true"
                    aria-invalid={errors.email ? "true" : "false"}
                  />
                  {errors.email && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <svg
                        className="w-5 h-5 text-red-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  )}
                </div>
                {errors.email && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400 flex items-center gap-1 font-medium animate-fade-in">
                    <svg
                      className="w-4 h-4 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {errors.email}
                  </p>
                )}
              </div>

              {/* Permanent Residency & Phone Number (side by side on desktop) */}
              <div className="relative group">
                <label
                  htmlFor="permanentResidency"
                  className="flex items-center gap-1 text-sm font-medium mb-2 text-black"
                >
                  <svg
                    className="w-4 h-4 text-blue-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Permanent Residency
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <svg
                      className="w-5 h-5 text-gray-400 dark:text-gray-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <select
                    id="permanentResidency"
                    name="permanentResidency"
                    value={formData.permanentResidency}
                    onChange={(e) => {
                      const selectedAlpha2 = e.target.value;
                      const country = countryList.find(
                        (c) => c.alpha2 === selectedAlpha2
                      );
                      const dialCode = country?.countryCallingCodes?.[0] || "";

                      setFormData((prev) => ({
                        ...prev,
                        permanentResidency: selectedAlpha2,
                      }));
                      setCode(dialCode);
                    }}
                    className="w-full pl-10 px-4 py-3 border-2 border-blue-200 dark:border-blue-800/40 rounded-xl bg-gray-50 dark:bg-gray-800/40 text-gray-900 dark:text-gray-200 transition-all duration-200 focus:bg-white dark:focus:bg-gray-800 focus:outline-none focus:ring-4 focus:ring-blue-100 dark:focus:ring-blue-900/30 focus:border-blue-500 dark:focus:border-blue-500"
                  >
                    <option value="">Select your country</option>
                    {countryList.map((country, i) => (
                      <option key={i} value={country.alpha2}>
                        {country.emoji} {country.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Phone Number (code + number, styled as one box) */}
              <div className="relative group">
                <label
                  htmlFor="phoneNumber"
                  className="flex items-center gap-1 text-sm font-medium mb-2 text-black"
                >
                  <svg
                    className="w-4 h-4 text-blue-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                  </svg>
                  Phone Number
                </label>
                <div className="flex w-full rounded-lg sm:rounded-xl overflow-hidden border-2 border-blue-200 dark:border-blue-800/40 transition-all focus-within:ring-4 focus-within:ring-blue-100 dark:focus-within:ring-blue-900/30 focus-within:border-blue-500 dark:focus-within:border-blue-500">
                  <div className="relative flex-shrink-0 w-2/5 sm:w-1/3 bg-gray-50 dark:bg-gray-800/40">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-2 sm:pl-3 pointer-events-none">
                      <svg
                        className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 dark:text-gray-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                      </svg>
                    </div>
                    <select
                      value={code}
                      onChange={(e) => {
                        const newCode = e.target.value;
                        setCode(newCode);
                        // Update the combined phone number in formData
                        const combinedPhoneNumber =
                          newCode && number
                            ? `${newCode}${number}`
                            : newCode || number || "";
                        setFormData((prev) => ({
                          ...prev,
                          phoneNumber: combinedPhoneNumber,
                        }));
                      }}
                      className="w-full h-full text-xs sm:text-sm pl-7 sm:pl-10 pr-1 sm:px-2 py-2 sm:py-3 bg-white dark:bg-gray-800 border-0 focus:outline-none focus:ring-0 text-gray-900 dark:text-gray-100"
                    >
                      <option
                        value=""
                        className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                      >
                        Code
                      </option>
                      {countryList.flatMap((c) =>
                        c.countryCallingCodes.map((cc) => (
                          <option
                            key={`${c.alpha2}-${cc}`}
                            value={cc}
                            className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                          >
                            {cc}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                  <input
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    value={number}
                    onChange={(e) => {
                      const newNumber = e.target.value;
                      setNumber(newNumber);
                      // Update the combined phone number in formData
                      const combinedPhoneNumber =
                        code && newNumber
                          ? `${code}${newNumber}`
                          : code || newNumber || "";
                      setFormData((prev) => ({
                        ...prev,
                        phoneNumber: combinedPhoneNumber,
                      }));
                    }}
                    placeholder="Enter number"
                    className="w-3/5 sm:w-2/3 text-xs sm:text-sm px-3 sm:px-4 py-2 sm:py-3 bg-white dark:bg-gray-800 border-0 border-l-2 border-blue-200 dark:border-blue-800/40 focus:outline-none focus:ring-0 text-gray-900 dark:text-gray-100"
                  />
                </div>
                {errors.phoneNumber && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                    <svg
                      className="w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {errors.phoneNumber}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Academic Information Section */}
          <div className="p-8 bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-2xl border border-blue-100 dark:border-blue-800/30 shadow-lg mb-10">
            <div className="flex items-center gap-4 mb-6 pb-4 border-b border-blue-100 dark:border-blue-800/30">
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/20">
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 14l9-5-9-5-9 5 9 5z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 14l6.16-3.422a12.22 12.22 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-black">
                  Academic Information
                </h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Tell us about your academic background and achievements
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Batch */}
              <div className="relative group">
                <label
                  htmlFor="batch"
                  className="flex items-center gap-1 text-xs sm:text-sm font-medium mb-2 text-black"
                >
                  <svg
                    className="w-3 h-3 sm:w-4 sm:h-4 text-indigo-500 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-black dark:text-indigo-400">
                    Graduation Year*
                  </span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <svg
                      className={`w-4 h-4 sm:w-5 sm:h-5 ${
                        errors.batch
                          ? "text-red-400 dark:text-red-500"
                          : "text-gray-400 dark:text-gray-500"
                      }`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <select
                    id="batch"
                    name="batch"
                    value={formData.batch || ""}
                    onChange={handleInputChange}
                    className={`w-full pl-9 sm:pl-10 px-3 sm:px-4 py-2 sm:py-3 text-sm border-2 rounded-lg sm:rounded-xl transition-all duration-300 bg-gray-50 dark:bg-gray-800/40 focus:bg-white dark:focus:bg-gray-800 focus:outline-none ${
                      errors.batch
                        ? "border-red-400 dark:border-red-500 text-red-600 dark:text-red-400 placeholder-red-400 dark:placeholder-red-500 focus:ring-2 focus:ring-red-200 dark:focus:ring-red-900/30 animate-pulse-once"
                        : "border-indigo-200 dark:border-indigo-800/40 text-gray-900 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-4 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 focus:border-indigo-500 dark:focus:border-indigo-500"
                    }`}
                    aria-required="true"
                    aria-invalid={errors.batch ? "true" : "false"}
                  >
                    <option value="">
                      {errors.batch
                        ? "Required field"
                        : "Select your graduation year"}
                    </option>
                    {Array.from({ length: 50 }, (_, i) => {
                      const year = new Date().getFullYear() - i;
                      return (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      );
                    })}
                  </select>
                  {errors.batch && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <svg
                        className="w-5 h-5 text-red-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  )}
                </div>
                {errors.batch && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400 flex items-center gap-1 font-medium animate-fade-in">
                    <svg
                      className="w-4 h-4 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {errors.batch}
                  </p>
                )}
              </div>

              {/* Academic Category */}
              <div className="relative group">
                <label
                  htmlFor="academicCategory"
                  className="flex items-center gap-1 text-xs sm:text-sm font-medium mb-2 text-black"
                >
                  <svg
                    className="w-3 h-3 sm:w-4 sm:h-4 text-indigo-500 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
                  </svg>
                  <span className="truncate text-gray-900 dark:text-gray-100">
                    Academic Category
                  </span>
                  <span className="text-red-500 dark:text-red-400 font-bold text-lg">
                    *
                  </span>
                  <span className="ml-1 text-xs font-semibold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 px-2 py-0.5 rounded-full">
                    Required
                  </span>
                </label>

                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <svg
                      className={`w-4 h-4 sm:w-5 sm:h-5 ${
                        errors.academicCategory
                          ? "text-red-400 dark:text-red-500"
                          : "text-gray-400 dark:text-gray-500"
                      }`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
                    </svg>
                  </div>
                  <select
                    id="academicCategory"
                    name="academicCategory"
                    value={formData.academicCategory || ""}
                    onChange={handleInputChange}
                    className={`w-full pl-9 sm:pl-10 px-3 sm:px-4 py-2 sm:py-3 text-sm border-2 rounded-lg sm:rounded-xl transition-all duration-300 bg-gray-50 dark:bg-gray-800/40 focus:bg-white dark:focus:bg-gray-800 focus:outline-none ${
                      errors.academicCategory
                        ? "border-red-400 dark:border-red-500 text-red-600 dark:text-red-400 placeholder-red-400 dark:placeholder-red-500 focus:ring-2 focus:ring-red-200 dark:focus:ring-red-900/30 animate-pulse-once"
                        : "border-indigo-200 dark:border-indigo-800/40 text-gray-900 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-4 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 focus:border-indigo-500 dark:focus:border-indigo-500"
                    }`}
                    aria-required="true"
                    aria-invalid={errors.academicCategory ? "true" : "false"}
                  >
                    <option value="">
                      {errors.academicCategory
                        ? "Required field"
                        : "Select your degree level"}
                    </option>
                    <option value="BSc">BSc (Bachelor of Science)</option>
                    <option value="MSc">MSc (Master of Science)</option>
                    <option value="PhD">PhD (Doctor of Philosophy)</option>
                    <option value="Diploma">Diploma</option>
                    <option value="Certificate">Certificate</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.academicCategory && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <svg
                        className="w-5 h-5 text-red-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  )}
                </div>

                {errors.academicCategory && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400 flex items-center gap-1 font-medium animate-fade-in">
                    <svg
                      className="w-4 h-4 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {errors.academicCategory}
                  </p>
                )}
              </div>

              {/* Show "Other" field ONLY if "Other" is selected - positioned under Academic Category box */}
              {formData.academicCategory === "Other" && (
                <div className="md:col-span-2">
                  <div className="relative group">
                    <label
                      htmlFor="academicCategoryOther"
                      className="flex items-center gap-1 text-xs sm:text-sm font-medium mb-2"
                    >
                      <svg
                        className="w-3 h-3 sm:w-4 sm:h-4 text-indigo-500 flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 13V5a2 2 0 00-2-2H4a2 2 0 00-2 2v8a2 2 0 002 2h3l3 3 3-3h3a2 2 0 002-2zM5 7a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zm1 3a1 1 0 100 2h3a1 1 0 100-2H6z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="truncate text-gray-900 dark:text-gray-100">
                        Please specify your Academic Category
                      </span>
                      <span className="text-red-500 dark:text-red-400 font-bold text-lg">
                        *
                      </span>
                      <span className="ml-1 text-xs font-semibold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 px-2 py-0.5 rounded-full">
                        Required
                      </span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <svg
                          className={`w-4 h-4 sm:w-5 sm:h-5 ${
                            errors.academicCategoryOther
                              ? "text-red-400 dark:text-red-500"
                              : "text-gray-400 dark:text-gray-500"
                          }`}
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 13V5a2 2 0 00-2-2H4a2 2 0 00-2 2v8a2 2 0 002 2h3l3 3 3-3h3a2 2 0 002-2zM5 7a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zm1 3a1 1 0 100 2h3a1 1 0 100-2H6z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <input
                        type="text"
                        id="academicCategoryOther"
                        name="academicCategoryOther"
                        value={formData.academicCategoryOther || ""}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            academicCategoryOther: e.target.value,
                          }))
                        }
                        className={`w-full pl-9 sm:pl-10 px-3 sm:px-4 py-2 sm:py-3 text-sm border-2 rounded-lg sm:rounded-xl transition-all duration-300 bg-gray-50 dark:bg-gray-800/40 focus:bg-white dark:focus:bg-gray-800 focus:outline-none ${
                          errors.academicCategoryOther
                            ? "border-red-400 dark:border-red-500 text-red-600 dark:text-red-400 placeholder-red-400 dark:placeholder-red-500 focus:ring-2 focus:ring-red-200 dark:focus:ring-red-900/30 animate-pulse-once"
                            : "border-indigo-200 dark:border-indigo-800/40 text-gray-900 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-4 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 focus:border-indigo-500 dark:focus:border-indigo-500"
                        }`}
                        placeholder={
                          errors.academicCategoryOther
                            ? "Required field"
                            : "Please specify your academic category"
                        }
                        aria-required="true"
                        aria-invalid={
                          errors.academicCategoryOther ? "true" : "false"
                        }
                      />
                      {errors.academicCategoryOther && (
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <svg
                            className="w-5 h-5 text-red-500"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                      )}
                    </div>
                    {errors.academicCategoryOther && (
                      <p className="mt-2 text-sm text-red-600 dark:text-red-400 flex items-center gap-1 font-medium animate-fade-in">
                        <svg
                          className="w-4 h-4 flex-shrink-0"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {errors.academicCategoryOther}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Academic Qualifications */}
              <div className="relative group">
                <label
                  htmlFor="academicQualifications"
                  className="flex items-center gap-1 text-xs sm:text-sm font-medium mb-2 text-black"
                >
                  <svg
                    className="w-3 h-3 sm:w-4 sm:h-4 text-indigo-500 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z"
                      clipRule="evenodd"
                    />
                    <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15a24.98 24.98 0 01-8-1.308z" />
                  </svg>
                  <span className="truncate text-gray-900 dark:text-gray-100">
                    Academic Qualifications
                  </span>
                  <span className="text-red-500 dark:text-red-400 font-bold text-lg">
                    *
                  </span>
                  <span className="ml-1 text-xs font-semibold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 px-2 py-0.5 rounded-full">
                    Required
                  </span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <svg
                      className={`w-4 h-4 sm:w-5 sm:h-5 ${
                        errors.academicQualifications
                          ? "text-red-400 dark:text-red-500"
                          : "text-gray-400 dark:text-gray-500"
                      }`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z"
                        clipRule="evenodd"
                      />
                      <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15a24.98 24.98 0 01-8-1.308z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    id="academicQualifications"
                    name="academicQualifications"
                    value={formData.academicQualifications || ""}
                    onChange={handleInputChange}
                    className={`w-full pl-9 sm:pl-10 px-3 sm:px-4 py-2 sm:py-3 text-sm border-2 rounded-lg sm:rounded-xl transition-all duration-300 bg-gray-50 dark:bg-gray-800/40 focus:bg-white dark:focus:bg-gray-800 focus:outline-none ${
                      errors.academicQualifications
                        ? "border-red-400 dark:border-red-500 text-red-600 dark:text-red-400 placeholder-red-400 dark:placeholder-red-500 focus:ring-2 focus:ring-red-200 dark:focus:ring-red-900/30 animate-pulse-once"
                        : "border-indigo-200 dark:border-indigo-800/40 text-gray-900 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-4 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 focus:border-indigo-500 dark:focus:border-indigo-500"
                    }`}
                    placeholder={
                      errors.academicQualifications
                        ? "Required field"
                        : "e.g., B.Phil (UK), B.Sc. Eng (Hons) (SL)"
                    }
                    aria-required="true"
                    aria-invalid={
                      errors.academicQualifications ? "true" : "false"
                    }
                  />
                  {errors.academicQualifications && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <svg
                        className="w-5 h-5 text-red-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  )}
                </div>
                {errors.academicQualifications ? (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400 flex items-center gap-1 font-medium animate-fade-in">
                    <svg
                      className="w-4 h-4 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {errors.academicQualifications}
                  </p>
                ) : (
                  <p className="mt-2 text-sm text-indigo-600 dark:text-indigo-400 bg-indigo-50/50 dark:bg-indigo-900/20 px-3 py-1 rounded-lg">
                    Include your specific degree title and any
                    honors/distinctions
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Professional Information Section */}
          <div className="p-8 bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-2xl border border-blue-100 dark:border-blue-800/30 shadow-lg mb-10 space-y-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-xl flex items-center justify-center shadow-md shadow-indigo-200 dark:shadow-indigo-900/30 transform rotate-3">
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H8a2 2 0 01-2-2V8a2 2 0 012-2V6"
                  />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-black">
                Professional Information
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
              {/* Designation */}
              <div className="relative group">
                <label
                  htmlFor="designation"
                  className="flex items-center gap-1 text-xs sm:text-sm font-medium mb-2 text-black"
                >
                  <svg
                    className="w-3 h-3 sm:w-4 sm:h-4 text-indigo-500 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z"
                      clipRule="evenodd"
                    />
                    <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15a24.98 24.98 0 01-8-1.308z" />
                  </svg>
                  <span className="truncate text-gray-900 dark:text-gray-100">
                    Current Designation
                  </span>
                  <span className="text-red-500 dark:text-red-400 font-bold text-lg">
                    *
                  </span>
                  <span className="ml-1 text-xs font-semibold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 px-2 py-0.5 rounded-full">
                    Required
                  </span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <svg
                      className={`w-4 h-4 sm:w-5 sm:h-5 ${
                        errors.designation
                          ? "text-red-400 dark:text-red-500"
                          : "text-gray-400 dark:text-gray-500"
                      }`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    id="designation"
                    name="designation"
                    value={formData.designation}
                    onChange={handleInputChange}
                    className={`w-full pl-9 sm:pl-10 px-3 sm:px-4 py-2 sm:py-3 text-sm border-2 rounded-lg sm:rounded-xl transition-all duration-300 bg-gray-50 dark:bg-gray-800/40 focus:bg-white dark:focus:bg-gray-800 focus:outline-none ${
                      errors.designation
                        ? "border-red-400 dark:border-red-500 text-red-600 dark:text-red-400 placeholder-red-400 dark:placeholder-red-500 focus:ring-2 focus:ring-red-200 dark:focus:ring-red-900/30 animate-pulse-once"
                        : "border-indigo-200 dark:border-indigo-800/40 text-gray-900 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-4 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 focus:border-indigo-500 dark:focus:border-indigo-500"
                    }`}
                    placeholder={
                      errors.designation
                        ? "Required field"
                        : "Enter your current job title"
                    }
                    aria-required="true"
                    aria-invalid={errors.designation ? "true" : "false"}
                  />
                  {errors.designation && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <svg
                        className="w-5 h-5 text-red-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  )}
                </div>
                {errors.designation && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400 flex items-center gap-1 font-medium animate-fade-in">
                    <svg
                      className="w-4 h-4 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {errors.designation}
                  </p>
                )}
              </div>

              {/* Company or Institute */}
              <div className="relative group">
                <label
                  htmlFor="companyOrInstitute"
                  className="flex items-center gap-1 text-xs sm:text-sm font-medium mb-2 text-black"
                >
                  <svg
                    className="w-3 h-3 sm:w-4 sm:h-4 text-indigo-500 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="truncate text-gray-900 dark:text-gray-100">
                    Company or Institute
                  </span>
                  <span className="text-red-500 dark:text-red-400 font-bold text-lg">
                    *
                  </span>
                  <span className="ml-1 text-xs font-semibold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 px-2 py-0.5 rounded-full">
                    Required
                  </span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <svg
                      className={`w-4 h-4 sm:w-5 sm:h-5 ${
                        errors.companyOrInstitute
                          ? "text-red-400 dark:text-red-500"
                          : "text-gray-400 dark:text-gray-500"
                      }`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <input
                    type="text"
                    id="companyOrInstitute"
                    name="companyOrInstitute"
                    value={formData.companyOrInstitute}
                    onChange={handleInputChange}
                    className={`w-full pl-9 sm:pl-10 px-3 sm:px-4 py-2 sm:py-3 text-sm border-2 rounded-lg sm:rounded-xl transition-all duration-300 bg-gray-50 dark:bg-gray-800/40 focus:bg-white dark:focus:bg-gray-800 focus:outline-none ${
                      errors.companyOrInstitute
                        ? "border-red-400 dark:border-red-500 text-red-600 dark:text-red-400 placeholder-red-400 dark:placeholder-red-500 focus:ring-2 focus:ring-red-200 dark:focus:ring-red-900/30 animate-pulse-once"
                        : "border-indigo-200 dark:border-indigo-800/40 text-gray-900 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-4 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 focus:border-indigo-500 dark:focus:border-indigo-500"
                    }`}
                    placeholder={
                      errors.companyOrInstitute
                        ? "Required field"
                        : "Enter your company or institute name"
                    }
                    aria-required="true"
                    aria-invalid={errors.companyOrInstitute ? "true" : "false"}
                  />
                  {errors.companyOrInstitute && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <svg
                        className="w-5 h-5 text-red-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  )}
                </div>
                {errors.companyOrInstitute && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400 flex items-center gap-1 font-medium animate-fade-in">
                    <svg
                      className="w-4 h-4 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {errors.companyOrInstitute}
                  </p>
                )}
              </div>

              {/* Category */}
              <div className="md:col-span-2 relative group">
                <label
                  htmlFor="category"
                  className="flex items-center gap-1 text-xs sm:text-sm font-medium mb-2 text-black"
                >
                  <svg
                    className="w-3 h-3 sm:w-4 sm:h-4 text-indigo-500 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
                  </svg>
                  <span className="truncate text-gray-900 dark:text-gray-100">
                    Professional Category
                  </span>
                  <span className="text-red-500 dark:text-red-400 font-bold text-lg">
                    *
                  </span>
                  <span className="ml-1 text-xs font-semibold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 px-2 py-0.5 rounded-full">
                    Required
                  </span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <svg
                      className={`w-4 h-4 sm:w-5 sm:h-5 ${
                        errors.category
                          ? "text-red-400 dark:text-red-500"
                          : "text-gray-400 dark:text-gray-500"
                      }`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
                    </svg>
                  </div>
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className={`w-full pl-9 sm:pl-10 px-3 sm:px-4 py-2 sm:py-3 text-sm border-2 rounded-lg sm:rounded-xl transition-all duration-300 bg-gray-50 dark:bg-gray-800/40 focus:bg-white dark:focus:bg-gray-800 focus:outline-none appearance-none ${
                      errors.category
                        ? "border-red-400 dark:border-red-500 text-red-600 dark:text-red-400 placeholder-red-400 dark:placeholder-red-500 focus:ring-2 focus:ring-red-200 dark:focus:ring-red-900/30 animate-pulse-once"
                        : "border-indigo-200 dark:border-indigo-800/40 text-gray-900 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-4 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 focus:border-indigo-500 dark:focus:border-indigo-500"
                    }`}
                    aria-required="true"
                    aria-invalid={errors.category ? "true" : "false"}
                  >
                    <option
                      value=""
                      className={
                        errors.category ? "text-red-400 dark:text-red-500" : ""
                      }
                    >
                      {errors.category
                        ? "Required field"
                        : "Select your professional category"}
                    </option>
                    {categoryOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    {errors.category ? (
                      <svg
                        className="w-5 h-5 text-red-500 mr-5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ) : null}
                    <svg
                      className={`w-5 h-5 ${
                        errors.category
                          ? "text-red-400"
                          : "text-gray-400 dark:text-gray-500"
                      }`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </div>
                {errors.category && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400 flex items-center gap-1 font-medium animate-fade-in">
                    <svg
                      className="w-4 h-4 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {errors.category}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Experience Section */}
          <div className="p-8 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl border border-blue-100 dark:border-blue-800/30 shadow-lg mb-10 space-y-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-md shadow-blue-200 dark:shadow-blue-900/30 transform -rotate-3">
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                  />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-black">
                Experience & Expertise
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
              {/* Fields of Experience */}
              <div className="relative group">
                <label
                  htmlFor="fieldsOfExperience"
                  className="flex items-center gap-1 text-sm font-medium text-gray-700 dark:text-gray-200 mb-2"
                >
                  <svg
                    className="w-4 h-4 text-blue-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z" />
                  </svg>
                  Fields of Experience
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <svg
                      className="w-5 h-5 text-gray-400 dark:text-gray-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    id="fieldsOfExperience"
                    name="fieldsOfExperience"
                    value={formData.fieldsOfExperience}
                    onChange={(e) =>
                      handleArrayInputChange(
                        "fieldsOfExperience",
                        e.target.value
                      )
                    }
                    className="w-full pl-10 px-4 py-3 border-2 border-blue-200 dark:border-blue-800/40 rounded-xl bg-gray-50 dark:bg-gray-800/40 text-gray-900 dark:text-gray-200 transition-all duration-200 focus:bg-white dark:focus:bg-gray-800 focus:outline-none focus:ring-4 focus:ring-blue-100 dark:focus:ring-blue-900/30 focus:border-blue-500 dark:focus:border-blue-500"
                    placeholder="e.g., Engineering, Technology, Business"
                  />
                </div>
                <p className="mt-2 text-sm text-blue-600 dark:text-blue-400 bg-blue-50/50 dark:bg-blue-900/20 px-3 py-1 rounded-lg">
                  Separate multiple fields with commas
                </p>
              </div>

              {/* Roles of Experience */}
              <div className="relative group">
                <label
                  htmlFor="rolesOfExperience"
                  className="flex items-center gap-1 text-sm font-medium text-gray-700 dark:text-gray-200 mb-2"
                >
                  <svg
                    className="w-4 h-4 text-blue-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z"
                      clipRule="evenodd"
                    />
                    <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15a24.98 24.98 0 01-8-1.308z" />
                  </svg>
                  Roles of Experience
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <svg
                      className="w-5 h-5 text-gray-400 dark:text-gray-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z"
                        clipRule="evenodd"
                      />
                      <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15a24.98 24.98 0 01-8-1.308z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    id="rolesOfExperience"
                    name="rolesOfExperience"
                    value={formData.rolesOfExperience}
                    onChange={(e) =>
                      handleArrayInputChange(
                        "rolesOfExperience",
                        e.target.value
                      )
                    }
                    className="w-full pl-10 px-4 py-3 border-2 border-blue-200 dark:border-blue-800/40 rounded-xl bg-gray-50 dark:bg-gray-800/40 text-gray-900 dark:text-gray-200 transition-all duration-200 focus:bg-white dark:focus:bg-gray-800 focus:outline-none focus:ring-4 focus:ring-blue-100 dark:focus:ring-blue-900/30 focus:border-blue-500 dark:focus:border-blue-500"
                    placeholder="e.g., Software Engineer, Project Manager, Consultant"
                  />
                </div>
                <p className="mt-2 text-sm text-blue-600 dark:text-blue-400 bg-blue-50/50 dark:bg-blue-900/20 px-3 py-1 rounded-lg">
                  Separate multiple roles with commas
                </p>
              </div>
            </div>
          </div>

          {/* Online Presence Section */}
          <div className="p-8 bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-2xl border border-purple-100 dark:border-purple-800/30 shadow-lg mb-10 space-y-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-md shadow-purple-200 dark:shadow-purple-900/30 transform rotate-6">
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9"
                  />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-black">Online Presence</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
              {/* LinkedIn URL */}
              <div className="relative group flex flex-col">
                <label
                  htmlFor="linkedinUrl"
                  className="flex items-center gap-1 text-sm font-medium text-gray-700 dark:text-gray-200 mb-2 min-h-[40px]"
                >
                  <svg
                    className="w-4 h-4 text-purple-500 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  LinkedIn Profile URL
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <svg
                      className="w-5 h-5 text-gray-400 dark:text-gray-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <input
                    type="text"
                    id="linkedinUrl"
                    name="linkedinUrl"
                    value={formData.linkedinUrl}
                    onChange={handleInputChange}
                    className={`w-full pl-10 px-4 py-3 border-2 rounded-xl transition-all duration-200 bg-gray-50 dark:bg-gray-800/40 focus:bg-white dark:focus:bg-gray-800 focus:outline-none ${
                      errors.linkedinUrl
                        ? "border-red-400 dark:border-red-500 text-red-600 dark:text-red-400 focus:ring-2 focus:ring-red-200 dark:focus:ring-red-900/30"
                        : "border-purple-200 dark:border-purple-800/40 text-gray-900 dark:text-gray-200 focus:ring-4 focus:ring-purple-100 dark:focus:ring-purple-900/30 focus:border-purple-500 dark:focus:border-purple-500"
                    }`}
                    placeholder="https://linkedin.com/in/yourprofile"
                  />
                </div>
                {errors.linkedinUrl && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                    <svg
                      className="w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {errors.linkedinUrl}
                  </p>
                )}
              </div>

              {/* Website URL */}
              <div className="relative group flex flex-col">
                <label
                  htmlFor="websiteUrl"
                  className="flex items-center gap-1 text-sm font-medium text-gray-700 dark:text-gray-200 mb-2 min-h-[40px]"
                >
                  <svg
                    className="w-4 h-4 text-purple-500 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012 6.012 0 011.912-2.706C6.512 5.73 6.974 6 7.5 6A1.5 1.5 0 019 7.5V8a2 2 0 004 0 2 2 0 011.523-1.943A5.977 5.977 0 0116 10c0 .34-.028.675-.083 1H15a2 2 0 00-2 2v2.197A5.973 5.973 0 0110 16v-2a2 2 0 00-2-2 2 2 0 01-2-2 2 2 0 00-1.668-1.973z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Personal/Company Website URL
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <svg
                      className="w-5 h-5 text-gray-400 dark:text-gray-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012 6.012 0 011.912-2.706C6.512 5.73 6.974 6 7.5 6A1.5 1.5 0 019 7.5V8a2 2 0 004 0 2 2 0 011.523-1.943A5.977 5.977 0 0116 10c0 .34-.028.675-.083 1H15a2 2 0 00-2 2v2.197A5.973 5.973 0 0110 16v-2a2 2 0 00-2-2 2 2 0 01-2-2 2 2 0 00-1.668-1.973z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <input
                    type="text"
                    id="websiteUrl"
                    name="websiteUrl"
                    value={formData.websiteUrl}
                    onChange={handleInputChange}
                    className={`w-full pl-10 px-4 py-3 border-2 rounded-xl transition-all duration-200 bg-gray-50 dark:bg-gray-800/40 focus:bg-white dark:focus:bg-gray-800 focus:outline-none ${
                      errors.websiteUrl
                        ? "border-red-400 dark:border-red-500 text-red-600 dark:text-red-400 focus:ring-2 focus:ring-red-200 dark:focus:ring-red-900/30"
                        : "border-purple-200 dark:border-purple-800/40 text-gray-900 dark:text-gray-200 focus:ring-4 focus:ring-purple-100 dark:focus:ring-purple-900/30 focus:border-purple-500 dark:focus:border-purple-500"
                    }`}
                    placeholder="https://yourwebsite.com"
                  />
                </div>
                {errors.websiteUrl && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                    <svg
                      className="w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {errors.websiteUrl}
                  </p>
                )}
              </div>
            </div>

            <div className="mt-6 text-center">
              <div className="px-4 py-2 rounded-lg bg-purple-50 dark:bg-purple-900/20 inline-flex items-center">
                <svg
                  className="w-5 h-5 text-purple-500 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-sm text-purple-700 dark:text-purple-300">
                  Including your professional links helps us connect and
                  showcase alumni achievements!
                </span>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="pt-10 mt-8 border-t border-blue-100 dark:border-blue-800/30">
            <div className="flex flex-col items-center justify-center space-y-6">
              <button
                type="submit"
                disabled={isSubmitting}
                className={`relative w-full md:w-auto px-10 sm:px-16 py-4 sm:py-5 rounded-xl font-bold text-white text-base sm:text-lg shadow-xl transition-all duration-300 transform hover:-translate-y-1 ${
                  isSubmitting
                    ? "bg-gray-400 dark:bg-gray-600 cursor-not-allowed"
                    : "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-blue-500/30 hover:shadow-blue-500/50"
                }`}
              >
                <div className="relative flex items-center justify-center gap-2">
                  {isSubmitting ? (
                    <>
                      <svg
                        className="animate-spin h-4 w-4 sm:h-5 sm:w-5"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      <span className="text-sm sm:text-base">
                        Processing Your Application...
                      </span>
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-5 h-5 sm:w-6 sm:h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                        />
                      </svg>
                      <span className="text-sm sm:text-base">
                        Submit Application
                      </span>
                    </>
                  )}
                </div>

                {/* Decorative animated particles */}
                <div className="absolute -top-1 -right-1 w-2 h-2 sm:w-3 sm:h-3 bg-blue-400 rounded-full opacity-70 animate-ping"></div>
                <div
                  className="absolute -bottom-1 -left-1 w-2 h-2 sm:w-3 sm:h-3 bg-indigo-400 rounded-full opacity-70 animate-ping"
                  style={{ animationDelay: "0.5s" }}
                ></div>
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

export default AlumniJoinForm;
